import { useContext } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { SignatureSchema, SignatureType } from '../../types';
import { styles } from '../../../../styles';
import { FinancialStatusContext } from '../../context/financial-status-context';
import Button from '../../../../_sharedComponents/button';
import StepHeader from '../../step-header';
import ErrorMessage from '../error-message';
import Label from '../label';

export default function Signature() {
	const financialContext = useContext(FinancialStatusContext);
	const todaysDate = new Date().toLocaleDateString();

	const defaultValues: SignatureType = {
		userExplanation: '',
		locationSignedAt: '',
		dateSigned: new Date().toISOString(), // Set to ISO 8601 format
		defendantSignature: '',
	};

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<SignatureType>({
		mode: 'onSubmit',
		reValidateMode: 'onChange',
		resolver: zodResolver(SignatureSchema),
		defaultValues,
	});

	const onSubmit = async (data: SignatureType) => {
		if (financialContext.isSubmitting) return;
		await financialContext.submitForm(data);
		financialContext.updateCurrentStep(9);
		scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<form className="h-full" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex h-full flex-col justify-between space-y-5">
				<StepHeader step={8} />
				<div>
					<div className="min-h-10 text-base font-light text-gray-500">
						I have these extraordinary financial conditions that prevent me from hiring a private lawyer (explain):
					</div>
					<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:flex-row md:space-x-20 md:space-y-0">
						{/* housing */}
						<div className="min-h-24 w-full md:w-1/2">
							<Label currentStep="signature" value="userExplanation" />
							<Controller
								control={control}
								name="userExplanation"
								render={({ field }) => (
									<textarea
										{...field}
										className={`${styles.textArea} ${errors.userExplanation ? styles.error : ''}`}
										id="userExplanation"
										placeholder={financialContext.getPlaceholder({ step: 'signature', value: 'userExplanation' })}
									/>
								)}
							/>
							{errors.userExplanation ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="userExplanation" />
								</div>
							) : null}
						</div>
					</div>
				</div>
				<div className="min-h-10 text-base font-light text-gray-500">
					I may be subject to criminal penalties for making any false statements in this declaration. I authorize the
					government agency that is responsible for providing lawyers to people who cannot pay in criminal cases to
					contact me or request information from me or a third party to verify whether I can afford a private lawyer. I
					may be ordered to pay the cost of my court appointed lawyer if the court later determines that I am able to
					pay. I declare under criminal penalty under the law of Utah that everything stated in this document is true.
				</div>
				<div>
					<div className="flex min-h-24 flex-col space-y-5 md:mt-0 md:flex-row md:space-x-20 md:space-y-0">
						{/* housing */}
						<div className="min-h-24 w-full md:w-1/2">
							<Label currentStep="signature" value="locationSignedAt" />
							<Controller
								control={control}
								name="locationSignedAt"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.largeInput} ${errors.locationSignedAt ? styles.error : ''}`}
										id="locationSignedAt"
										placeholder={financialContext.getPlaceholder({ step: 'signature', value: 'locationSignedAt' })}
									/>
								)}
							/>
							{errors.locationSignedAt ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="signature" value="locationSignedAt" />
								</div>
							) : null}
						</div>
					</div>
				</div>
				<div className="text-base font-light text-gray-500">
					{financialContext.language === 'en' ? 'Date form is being submitted:' : 'Fecha de envío del formulario:'}{' '}
					{todaysDate}
				</div>
				<div>
					<div className="flex min-h-24 flex-col space-y-5 md:mt-0 md:flex-row md:space-x-20 md:space-y-0">
						{/* defendantSignature */}
						<div className="min-h-24 w-full md:w-1/2">
							<Label currentStep="signature" value="defendantSignature" />
							<Controller
								control={control}
								name="defendantSignature"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.defendantSignature ? styles.error : ''}`}
										id="defendantSignature"
										placeholder={financialContext.getPlaceholder({ step: 'signature', value: 'defendantSignature' })}
									/>
								)}
							/>
							{errors.defendantSignature ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="signature" value="defendantSignature" />
								</div>
							) : null}
						</div>
					</div>
				</div>
				<div className="mx-auto my-10 flex space-x-10">
					<Button
						label={financialContext.language === 'en' ? 'Prev' : 'Anterior'}
						onClick={() => {
							financialContext.updateCurrentStep(7);
							scrollTo({ top: 0, behavior: 'smooth' });
						}}
						type="button"
					/>
					<Button
						disabled={financialContext.isSubmitting}
						label={financialContext.language === 'en' ? 'Submit' : 'Enviar'}
						type="submit"
					/>
				</div>
				<div className="mx-auto flex w-full justify-center">
					{financialContext.hasSubmissionError ? (
						<div className="text-lg font-semibold text-red-950">
							{financialContext.language === 'en'
								? 'There was an error submitting the form. Please try again.'
								: 'Hubo un error al enviar el formulario. Por favor, inténtelo de nuevo.'}
						</div>
					) : null}
				</div>
			</div>
		</form>
	);
}
